<template>
  <div class="lg:container lg:mx-auto">
    <Head :title="project.title" />
    <div class="pr-4">
      <span class="text-sm">Project</span>
      <h1 class="text-4xl font-bold">{{ project.title }}</h1>
      <span class="text-sm">Run name</span>
      <h1 class="mb-8 text-xl font-bold">{{ run.name }}</h1>
    </div>
    <!-- Title -->

    <div class="w-full flex mb-4">
      <div class="flex-auto mr-10">
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">System</p>
          <p class="text-blue-400">{{ run.title }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Drainage Width</p>
          <p class="text-blue-400">{{ run.width }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Drainage Edge</p>
          <p class="text-blue-400">{{ run.edge }}</p>
        </div>
        <div class="flex items-center mb-2 py-2">
          <p class="font-bold text-black w-2/4 text-xl">Grate</p>
          <p class="text-blue-400">{{ run.grate }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Load Class</p>
          <p class="text-blue-400 flex items-center">
            <class-icon :name="run.loadClass.icon" class="flex-shrink-0 mx-2 w-8 h-8 fill-blue-800" />
            {{ run.loadClass.name }}
          </p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Material</p>
          <p class="text-blue-400"> {{ run.material }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Compliances</p>
          <div class="text-blue-400 flex items-center">
            <div
              v-for="c in run.compliances"
              :key="c.id"
              class="border rounded-md py-1 border-blue-400  flex items-center mx-0.5" :alt="c.name"
            >
              <compliance-icon :name="c.icon" class="flex-shrink-0 mx-1 w-4 h-4 fill-blue-800" />
            </div>
          </div>
        </div>
      </div>
      <div class="flex-auto">
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Total Run Length</p>
          <p class="text-blue-400"> {{ run.length }}</p>
        </div>
        <div v-if="run.channel_depth" class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Overall Channel Depth:</p>
          <p class="text-blue-400"> {{ run.channel_depth }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Constant Depth or Sloped Depth</p>
          <p class="text-blue-400">{{ run.depth }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Outlet Configuration</p>
          <p class="text-blue-400"> {{ run.outlet }}</p>
        </div>
        <div class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-black w-2/4">Outlet Type</p>
          <p class="text-blue-400"> {{ run.outlet_type }} </p>
        </div>
        <div v-if="run.outlet_size" class="flex items-center mb-2 border-b py-2">
          <p class="font-bold text-back w-2/4">Outlet Size</p>
          <p class="text-blue-400"> {{ run.outlet_size }} </p>
        </div>
      </div>
    </div>
    <!-- Run details -->

    <div class="flex items-center border-solid border-t border-blue-800 my-2"> &nbsp;</div>

    <div class="flex flex-row justify-between aligns-center">
      <div class="w-full">
        <ul class="flex flex-row items-center">
          <li class="mx-1">
            <div v-if="run.permissions.update" class="flex flex-row items-center">
              <select id="discount-multiplier" v-model="discount" name="discount-multiplier" class="form-select" @change="applyDiscount()">
                <option :value="null" selected>Discount Multiplier</option>
                <option v-for="d in project.discounts" :key="d.id" :value="d.id">{{ d.value }}</option>
              </select>
              <button v-if="discount" class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="discount = null, applyDiscount()">X</button>
            </div>
          </li>
          <li class="mx-1">
            <download-button :run-id="run.id" :label="true" />
          </li>
          <li v-if="run.permissions.update" class="mx-1">
            <a class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" :href="`/runs/${run.id}/edit`">View/Edit Drawing</a>
          </li>
          <li v-if="run.permissions.create" class="mx-1">
            <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" type="button" @click="clone(run.id)">
              Duplicate Run
            </button>
          </li>
          <li v-if="! run.deleted_at && run.permissions.delete" class="mx-1">
            <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="destroy(run.id)">Delete Run</button>
          </li>
          <li v-if="run.deleted_at && run.permissions.restore" class="mx-1">
            <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="restore(run.id)">Restore Run</button>
          </li>
        </ul>
      </div>
      <div class="w-2/4  flex flex-row mb-8 justify-end items-center">
        <Link class="btn-blue" :href="`/projects/${project.id}`">
          <span> Back to Project</span>
        </Link>
      </div>
    </div>
    <!-- Bulk Actions -->

    <div class="overflow-x-auto mb-12">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold text-lg">
            <th class="pt-2 pr-4">Part</th>
            <th class="pt-2 pr-4">Description</th>
            <th class="pt-2 px-4 text-center">Price</th>
            <th class="pt-2 px-4 text-center">Quantity</th>
            <th class="pt-2 text-center">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(part, index) in run.parts" :key="index"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
            :class="index % 2 == 0 ? '' : 'bg-gray-100'"
          >
            <td>
              <span class="flex items-center p-2 focus:text-blue-500">
                {{ part.part_number }}
                <icon v-if="part.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </span>
            </td>
            <td>
              {{ part.description }}
            </td>
            <td class="text-right">
              ${{ part.price }}
            </td>
            <td class="text-center">
              {{ part.qty }}
            </td>
            <td class="text-right font-bold">
              $ {{ part.total_str }}
            </td>
          </tr>
          <tr v-if="run.parts.length === 0">
            <td class="px-6 py-4 border-t text-center text-xl" colspan="6">No parts found.</td>
          </tr>
        </tbody>
        <tfoot v-if="run.total">
          <tr class="border-t">
            <td class="text-right" colspan="3">&nbsp;</td>
            <td class="text-center  pt-2 ">Total</td>
            <td class="text-right font-bold  pt-2">$ {{ run.total }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Notes -->
    <div class="flex items-center border-b pb-2">
      <h4 class="font-bold text-lg mr-4">Run notes</h4>
      <create-note v-if="run.permissions.notes.create" :project="project" :run="run" />
    </div>

    <table class="w-full text-left mb-12">
      <thead>
        <tr>
          <th class="py-3 w-3/5">Note</th>
          <th class="py-3">Author</th>
          <th class="py-3">Updated</th>
          <th class="py-3">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="note in run.notes" :key="note.id">
          <td class="border-t py-2">
            <a v-if="note.permissions.update" class="flex items-center focus:text-blue-500 cursor-pointer" :class="{'line-through': note.deleted_at}" @click="noteToEdit = note">
              {{ note.description }}
              <icon v-if="note.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
            </a>
            <span v-else>{{ note.description }}</span>
          </td>
          <td class="border-t py-2">
            {{ note.user }}
          </td>
          <td class="border-t py-2">
            {{ note.updated_at }}
          </td>
          <td class="border-t py-2">
            <div class="flex items-center">
              <a v-if="note.permissions.update" class="p-2 cursor-pointer" @click="noteToEdit = note"><icon name="edit" class="block w-4 h-4" /></a>
              <button v-if="! note.deleted_at && note.permissions.delete" class="p-2" type="button" @click="destroyNote(note.id)">
                <icon name="trash" class="block w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <edit-note v-if="noteToEdit && noteToEdit.permissions.update" class="p-2" :note="noteToEdit" @close="noteToEdit = null" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import ClassIcon from '@/Shared/LoadClassesIcon'
import ComplianceIcon from '@/Shared/CompliancesIcon'
import DownloadButton from './Partials/DownloadButton'
import CreateNote from './../Notes/Create'
import EditNote from './../Notes/Edit'
export default {
  components: {
    Head,
    Icon,
    Link,
    ClassIcon,
    ComplianceIcon,
    DownloadButton,
    CreateNote,
    EditNote,
  },
  layout: Layout,
  props: {
    project    : Object,
    run       : Object,
  },
  data () {
    return {
      discount: this.run.discount,
      noteToEdit: null,
    }
  },
  methods:{
    applyDiscount() {
      this.$inertia.put(`/runs/${this.run.id}/discount`, {
        discount_id: this.discount ,
      },{
        onSuccess: () => {},
      })
    },
    clone(id) {
      if (confirm('Are you sure you want to duplicate this run?')) {
        this.$inertia.put(`/runs/${id}/duplicate`, {
          onSuccess: () => {},
        })
      }
    },
    destroy(id) {
      if (confirm('Are you sure you want to delete this run?')) {
        this.$inertia.delete(`/runs/${id}`, {
          onSuccess: () => {},
        })
      }
    },
    restore(id) {
      if (confirm('Are you sure you want to restore this run?')) {
        this.$inertia.put(`/runs/${id}/restore`, {
          onSuccess: () => {},
        })
      }
    },
    destroyNote(id) {
      if (confirm('Are you sure you want to delete this note?')) {
        this.$inertia.delete(`/notes/${id}`, {
          onSuccess: () => {},
        })
      }
    },
  },
}
</script>
